/**
 * Accessibility Framework
 *
 * Purpose: Centralized accessibility utilities for WCAG 2.1 AA compliance
 * Created: October 31, 2025
 * Status: Production Ready
 *
 * Features:
 * - ARIA live region announcements
 * - Form validation with aria-invalid + aria-describedby
 * - Focus management utilities
 * - Screen reader helpers
 *
 * Usage:
 * window.a11y.announce('Item added to cart');
 * window.a11y.setFieldError(inputElement, 'Invalid email');
 */

// Guard to prevent duplicate initialization
if (typeof window.AccessibilityFramework === 'undefined') {

class AccessibilityFramework {
  constructor() {
    this.liveRegions = {};
    this.debug = window.location.hostname === 'localhost' ||
                 window.location.hostname.includes('127.0.0.1') ||
                 window.location.search.includes('debug=true');

    this.log('Accessibility Framework initializing...');
    this.init();
  }

  /**
   * Initialize framework
   */
  init() {
    // Create ARIA live regions on page load
    this.createLiveRegions();

    // Add .sr-only utility class if not already defined
    this.ensureSROnlyStyles();

    this.log('Accessibility Framework initialized');
  }

  /**
   * Create ARIA live regions for announcements
   * - Polite: Non-urgent announcements (cart updates, etc.)
   * - Assertive: Urgent announcements (errors, warnings)
   */
  createLiveRegions() {
    const priorities = ['polite', 'assertive'];

    priorities.forEach(priority => {
      if (!document.querySelector(`[data-a11y-live="${priority}"]`)) {
        const region = document.createElement('div');
        region.setAttribute('aria-live', priority);
        region.setAttribute('aria-atomic', 'true');
        region.setAttribute('data-a11y-live', priority);
        region.className = 'sr-only';
        document.body.appendChild(region);

        this.liveRegions[priority] = region;
        this.log(`Created ${priority} live region`);
      } else {
        this.liveRegions[priority] = document.querySelector(`[data-a11y-live="${priority}"]`);
      }
    });
  }

  /**
   * Announce message to screen readers
   * @param {string} message - Message to announce
   * @param {string} priority - 'polite' or 'assertive'
   * @param {number} duration - How long to display message (ms)
   */
  announce(message, priority = 'polite', duration = 1000) {
    if (!message) {
      window.debugLogger.warn('AccessibilityFramework: No message provided for announcement');
      return;
    }

    const region = this.liveRegions[priority];
    if (!region) {
      window.debugLogger.error(`AccessibilityFramework: Live region "${priority}" not found`);
      return;
    }

    // Set message
    region.textContent = message;
    this.log(`📢 Announced (${priority}): "${message}"`);

    // Clear after duration
    setTimeout(() => {
      region.textContent = '';
    }, duration);
  }

  /**
   * Announce success message
   * @param {string} message - Success message
   */
  announceSuccess(message) {
    this.announce(message, 'polite');
  }

  /**
   * Announce error message
   * @param {string} message - Error message
   */
  announceError(message) {
    this.announce(`Error: ${message}`, 'assertive');
  }

  /**
   * Set form field error with ARIA attributes
   * @param {HTMLElement} field - Input element
   * @param {string} errorMessage - Error message
   */
  setFieldError(field, errorMessage) {
    if (!field || !errorMessage) {
      window.debugLogger.warn('AccessibilityFramework: Field or error message missing');
      return;
    }

    // Set aria-invalid
    field.setAttribute('aria-invalid', 'true');

    // Create or update error message element
    const errorId = `${field.id || this.generateId()}-error`;
    let errorEl = document.getElementById(errorId);

    if (!errorEl) {
      errorEl = document.createElement('span');
      errorEl.id = errorId;
      errorEl.className = 'field-error sr-only';
      field.parentNode.insertBefore(errorEl, field.nextSibling);
    }

    errorEl.textContent = errorMessage;

    // Link field to error message
    field.setAttribute('aria-describedby', errorId);

    // Announce error
    this.announceError(errorMessage);

    this.log(`Set error on field ${field.id || field.name}: "${errorMessage}"`);
  }

  /**
   * Clear field error
   * @param {HTMLElement} field - Input element
   */
  clearFieldError(field) {
    if (!field) return;

    field.removeAttribute('aria-invalid');

    const errorId = field.getAttribute('aria-describedby');
    if (errorId) {
      field.removeAttribute('aria-describedby');

      const errorEl = document.getElementById(errorId);
      if (errorEl && errorEl.classList.contains('field-error')) {
        errorEl.remove();
      }
    }

    this.log(`Cleared error on field ${field.id || field.name}`);
  }

  /**
   * Trap focus within element (uses existing trapFocus from global.js)
   * @param {HTMLElement} container - Container to trap focus within
   * @param {HTMLElement} elementToFocus - Element to focus initially
   */
  trapFocus(container, elementToFocus) {
    if (typeof trapFocus === 'function') {
      trapFocus(container, elementToFocus);
      this.log('Focus trapped in container');
    } else {
      window.debugLogger.warn('AccessibilityFramework: trapFocus function not found (global.js may not be loaded)');
    }
  }

  /**
   * Remove focus trap (uses existing removeTrapFocus from global.js)
   * @param {HTMLElement} elementToFocus - Element to focus after removing trap
   */
  removeTrapFocus(elementToFocus) {
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(elementToFocus);
      this.log('Focus trap removed');
    } else {
      window.debugLogger.warn('AccessibilityFramework: removeTrapFocus function not found');
    }
  }

  /**
   * Set element ARIA label
   * @param {HTMLElement} element - Element to label
   * @param {string} label - ARIA label text
   */
  setAriaLabel(element, label) {
    if (!element || !label) return;

    element.setAttribute('aria-label', label);
    this.log(`Set aria-label on element: "${label}"`);
  }

  /**
   * Set element ARIA described by
   * @param {HTMLElement} element - Element to describe
   * @param {string} describerId - ID of describing element
   */
  setAriaDescribedBy(element, describerId) {
    if (!element || !describerId) return;

    element.setAttribute('aria-describedby', describerId);
    this.log(`Set aria-describedby on element: ${describerId}`);
  }

  /**
   * Mark element as expanded/collapsed
   * @param {HTMLElement} element - Toggle element
   * @param {boolean} isExpanded - Whether element is expanded
   */
  setAriaExpanded(element, isExpanded) {
    if (!element) return;

    element.setAttribute('aria-expanded', String(isExpanded));
    this.log(`Set aria-expanded="${isExpanded}" on element`);
  }

  /**
   * Mark element as current (for navigation)
   * @param {HTMLElement} element - Navigation element
   * @param {string} value - 'page', 'step', 'location', 'date', 'time', or 'true'
   */
  setAriaCurrent(element, value = 'page') {
    if (!element) return;

    element.setAttribute('aria-current', value);
    this.log(`Set aria-current="${value}" on element`);
  }

  /**
   * Ensure .sr-only utility class exists
   * (Screen reader only - visually hidden)
   */
  ensureSROnlyStyles() {
    if (document.querySelector('style[data-a11y-sr-only]')) {
      return; // Already exists
    }

    const style = document.createElement('style');
    style.setAttribute('data-a11y-sr-only', 'true');
    style.textContent = `
      .sr-only, .visually-hidden {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        margin: -1px !important;
        padding: 0 !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
      }
    `;
    document.head.appendChild(style);

    this.log('Added .sr-only utility styles');
  }

  /**
   * Generate unique ID for elements
   */
  generateId() {
    return `a11y-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Debug logging
   */
  log(message, ...args) {
    if (this.debug) {
      window.debugLogger.log(`[A11Y] ${message}`, ...args);
    }
  }

  /**
   * Get focusable elements within container
   * (Uses existing getFocusableElements from global.js if available)
   * @param {HTMLElement} container - Container to search within
   * @returns {Array} Array of focusable elements
   */
  getFocusableElements(container) {
    if (typeof getFocusableElements === 'function') {
      return getFocusableElements(container);
    }

    // Fallback implementation
    const selector = 'a[href], button:enabled, input:enabled, select:enabled, textarea:enabled, [tabindex]:not([tabindex="-1"])';
    return Array.from(container.querySelectorAll(selector));
  }

  /**
   * Focus first focusable element in container
   * @param {HTMLElement} container - Container to search within
   */
  focusFirstElement(container) {
    const focusable = this.getFocusableElements(container);
    if (focusable.length > 0) {
      focusable[0].focus();
      this.log('Focused first element in container');
    }
  }

  /**
   * Check if element is visible to screen readers
   * @param {HTMLElement} element - Element to check
   * @returns {boolean} True if visible to screen readers
   */
  isAccessible(element) {
    if (!element) return false;

    // Check if element is hidden
    if (element.hasAttribute('aria-hidden') && element.getAttribute('aria-hidden') === 'true') {
      return false;
    }

    // Check if element is visually hidden but accessible
    const styles = window.getComputedStyle(element);
    if (styles.display === 'none' || styles.visibility === 'hidden') {
      return false;
    }

    return true;
  }
}

// Create global instance
window.AccessibilityFramework = AccessibilityFramework;
window.a11y = new AccessibilityFramework();

} // End guard

/**
 * USAGE EXAMPLES:
 *
 * // Announce message to screen readers
 * window.a11y.announce('Item added to cart');
 * window.a11y.announceSuccess('Form submitted successfully');
 * window.a11y.announceError('Please fill in required fields');
 *
 * // Form validation
 * const emailInput = document.querySelector('[name="email"]');
 * window.a11y.setFieldError(emailInput, 'Please enter a valid email address');
 * window.a11y.clearFieldError(emailInput);
 *
 * // ARIA attributes
 * window.a11y.setAriaLabel(button, 'Close cart drawer');
 * window.a11y.setAriaExpanded(button, true);
 * window.a11y.setAriaCurrent(navLink, 'page');
 *
 * // Focus management
 * window.a11y.trapFocus(dialogElement, firstButton);
 * window.a11y.removeTrapFocus(triggerButton);
 * window.a11y.focusFirstElement(container);
 */
